/**
 * 声音：入场的心跳“咚—哒”、爆炸声，和背景音乐。全部用 Web Audio 实时合成，没有音频文件。做法同原型。
 *
 * 背景音乐默认是程序生成的氛围乐「星海晚风」（Cmaj9 · Am9 · Fmaj9 · G6/9 循环的长音铺底加音乐盒），没有版权问题。
 * 可以换成本机的一首歌：只解码在内存里，不上传、不存进浏览器，刷新后回到默认（docs/design.md §3.6）。
 * 音乐和音效共用一个开关，按设备记在 localStorage；页面隐藏时暂停。
 * 浏览器只允许在用户点过之后出声，所以要先在点击、按键的回调里 unlock()；没点过的话所有调用都静默。
 */

export interface Sound {
  /** 背景音乐和入场音效的总开关 */
  readonly enabled: boolean;
  /** 现在放的这首 */
  readonly name: string;
  /** 换过歌（只在这次打开时有效） */
  readonly custom: boolean;
  /** 在点击、按键的回调里调用；返回能不能出声 */
  unlock(): boolean;
  /** 开始放背景音乐；关着、没点过或者已经在放时什么也不做 */
  start(fadeSec?: number): void;
  /** 打开或关上音乐和音效；在点击回调里调用 */
  setEnabled(on: boolean): void;
  useFile(file: File): Promise<void>;
  useDefault(): void;
  /** 一次心跳：period 秒内先强后弱两下 */
  heartbeat(period: number, strength?: number): void;
  boom(): void;
  /** 一串上行的亮音（爆炸声的后半段）：Ta 赴约的回响 */
  chime(): void;
  /** 漫游到站：一声很轻的泛音，比 chime 轻很多 */
  roamTick(): void;
  /** 长按拆时间胶囊：一段越来越高、越来越响的低音；返回停下它的函数（没按满就松手） */
  charge(seconds: number): () => void;
  /** 时间胶囊碎开：清脆的一阵冰晶碎裂 */
  shatter(): void;
  /** 防借看时整体静音（不改开关、不记住），关掉伪装再恢复 */
  hush(on: boolean): void;
  /** 看照片、听语音时让背景乐变轻；录音时连音效一起静音。返回幂等的释放函数。 */
  hold(mode: SoundHold): () => void;
}

export type SoundHold = 'photo' | 'voice' | 'recording';

const KEY = 'star-sea/music';
const DEFAULT_NAME = '星海晚风（程序生成）';
/** 自己的歌整首解码在内存里，一首 4 分钟的歌约 90 MB；太大的文件手机上会撑爆 */
const MAX_FILE = 30 * 1024 * 1024;
/** 66 拍/分的八分音符 */
const EIGHTH = 60 / 66 / 2;
/** Cmaj9 · Am9 · Fmaj9 · G6/9，各两小节 */
const CHORDS = [
  [48, 55, 59, 62, 64],
  [45, 52, 55, 59, 60],
  [41, 48, 52, 55, 57],
  [43, 50, 55, 57, 62],
];
/** C 大调五声，音乐盒的音 */
const MEL = [72, 74, 76, 79, 81, 84, 86, 88];

const hz = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

function readEnabled(): boolean {
  try {
    return localStorage.getItem(KEY) !== '0';
  } catch {
    return true;
  }
}

function saveEnabled(on: boolean): void {
  try {
    localStorage.setItem(KEY, on ? '1' : '0');
  } catch {
    // 浏览器不让存：这次打开时有效
  }
}

export function createSound(): Sound {
  let ctx: AudioContext | null = null;
  let master: GainNode, musicBus: GainNode, sfxBus: GainNode, reverb: ConvolverNode;
  let enabled = readEnabled(), unlocked = false, playing = false, hushed = false;
  let timer = 0, step = 0, nextT = 0;
  let buffer: AudioBuffer | null = null, src: AudioBufferSourceNode | null = null, name = DEFAULT_NAME;
  const holds = new Map<symbol, SoundHold>();

  const live = () => ctx !== null && unlocked && enabled;
  const recording = () => [...holds.values()].includes('recording');
  const effectLive = () => live() && !hushed && !recording();
  const musicLevel = () => (buffer ? 0.7 : 0.9) * Math.min(1, ...[...holds.values()].map((mode) => mode === 'recording' ? 0 : mode === 'voice' ? 0.12 : 0.35));

  function ensure(): AudioContext | null {
    if (ctx) return ctx;
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    const c = (ctx = new AC());
    master = c.createGain();
    master.gain.value = !enabled || hushed || recording() ? 0 : 0.9;
    master.connect(c.destination);
    musicBus = c.createGain();
    musicBus.gain.value = 0;
    musicBus.connect(master);
    sfxBus = c.createGain();
    sfxBus.gain.value = 0.85;
    sfxBus.connect(master);
    // 程序生成的混响；湿声走音乐总线，跟着音乐一起淡入淡出（同原型）
    const len = Math.floor(c.sampleRate * 3.4), ir = c.createBuffer(2, len, c.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = ir.getChannelData(ch);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.6);
    }
    reverb = c.createConvolver();
    reverb.buffer = ir;
    const wet = c.createGain();
    wet.gain.value = 0.5;
    reverb.connect(wet);
    wet.connect(musicBus);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) void c.suspend();
      else if (playing && unlocked) void c.resume();
    });
    return c;
  }

  /** 一个音：起音 a 秒到 peak，保持 hold 秒，再用 rel 秒衰减 */
  function voice(type: OscillatorType, f: number, t: number, a: number, peak: number, hold: number, rel: number, dests: AudioNode[], cutoff = 0, pan = 0, detune = 0): void {
    const c = ctx!, o = c.createOscillator(), g = c.createGain();
    o.type = type;
    o.frequency.value = f;
    o.detune.value = detune;
    let node: AudioNode = o;
    if (cutoff) {
      const lp = c.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = cutoff;
      o.connect(lp);
      node = lp;
    }
    node.connect(g);
    let out: AudioNode = g;
    if (pan && c.createStereoPanner) {
      const p = c.createStereoPanner();
      p.pan.value = pan;
      g.connect(p);
      out = p;
    }
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(peak, t + a);
    g.gain.setValueAtTime(peak, t + a + hold);
    g.gain.exponentialRampToValueAtTime(0.0001, t + a + hold + rel);
    for (const d of dests) out.connect(d);
    o.start(t);
    o.stop(t + a + hold + rel + 0.05);
  }

  /** 生成的音乐：往前排 0.7 秒的音符，每 120 毫秒看一次 */
  function schedule(): void {
    const c = ctx!, ahead = c.currentTime + 0.7;
    while (nextT < ahead) {
      const t = nextT, chord = CHORDS[Math.floor(step / 16) % CHORDS.length]!;
      if (step % 16 === 0) {
        // 换和弦：柔和的长音铺底 + 一个低音
        chord.forEach((m, k) => {
          voice('triangle', hz(m), t, 1.6, 0.03, 16 * EIGHTH - 1.2, 2.6, [musicBus, reverb], 1100, (k - 2) * 0.18, (Math.random() - 0.5) * 8);
          voice('sine', hz(m), t, 1.8, 0.02, 16 * EIGHTH - 1.4, 2.8, [reverb], 0, 0, 7);
        });
        voice('sine', hz(chord[0]! - 12), t, 0.4, 0.05, 16 * EIGHTH - 0.6, 2.2, [musicBus], 400);
      }
      // 音乐盒：强拍更常出现
      const chance = step % 4 === 0 ? 0.62 : step % 2 === 0 ? 0.34 : 0.18;
      if (Math.random() < chance) {
        const pool = MEL.filter((m) => chord.some((n) => (m - n) % 12 === 0) || Math.random() < 0.35);
        const m = pool[Math.floor(Math.random() * pool.length)];
        if (m !== undefined) {
          voice('sine', hz(m), t, 0.006, 0.05, 0.05, 1.7, [musicBus, reverb], 0, (Math.random() - 0.5) * 0.8);
          voice('triangle', hz(m) * 2, t, 0.004, 0.008, 0.02, 0.8, [reverb], 3000);
        }
      }
      nextT += EIGHTH;
      step++;
    }
  }

  function stopSources(): void {
    clearInterval(timer);
    timer = 0;
    try {
      src?.stop();
    } catch {
      // 已经停了
    }
    src = null;
  }

  function startSource(): void {
    const c = ctx!;
    stopSources();
    if (buffer) {
      src = c.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.connect(musicBus);
      src.start();
    } else {
      step = 0;
      nextT = c.currentTime + 0.1;
      schedule();
      timer = window.setInterval(schedule, 120);
    }
  }

  function fadeTo(v: number, sec: number): void {
    const g = musicBus.gain, t = ctx!.currentTime;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(v, t + sec);
  }

  function refreshMaster(): void {
    if (!ctx) return;
    const g = master.gain, t = ctx.currentTime;
    g.cancelScheduledValues(t);
    // 在申请麦克风前同步静音，防止渐弱的尾音被新录音收进去。
    if (recording()) g.setValueAtTime(0, t);
    else {
      g.setValueAtTime(g.value, t);
      g.linearRampToValueAtTime(!enabled || hushed ? 0 : 0.9, t + (hushed ? 0.15 : 1.2));
    }
  }

  function hold(mode: SoundHold): () => void {
    const id = Symbol(mode);
    holds.set(id, mode);
    const refresh = () => {
      if (!ctx) return;
      fadeTo(playing && enabled ? musicLevel() : 0, 0.4);
      refreshMaster();
    };
    refresh();
    return () => {
      if (holds.delete(id)) refresh();
    };
  }

  function play(fadeSec: number): void {
    if (!live()) return;
    void ctx!.resume();
    if (!playing) {
      playing = true;
      startSource();
    }
    fadeTo(musicLevel(), fadeSec);
  }

  function pause(fadeSec = 1.2): void {
    if (!ctx || !playing) return;
    playing = false;
    fadeTo(0, fadeSec);
    setTimeout(() => {
      if (!playing) stopSources();
    }, fadeSec * 1000 + 60);
  }

  function unlock(): boolean {
    const c = ensure();
    if (!c) return false;
    void c.resume();
    unlocked = true;
    return true;
  }

  function thump(t: number, k: number): void {
    const c = ctx!, o = c.createOscillator(), g = c.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(78, t);
    o.frequency.exponentialRampToValueAtTime(42, t + 0.16);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(k, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.26);
    o.connect(g);
    g.connect(sfxBus);
    o.start(t);
    o.stop(t + 0.3);
  }

  return {
    get enabled() {
      return enabled;
    },
    get name() {
      return name;
    },
    get custom() {
      return buffer !== null;
    },
    unlock,
    start(fadeSec = 2.5) {
      if (!playing) play(fadeSec);
    },
    setEnabled(on) {
      enabled = on;
      saveEnabled(on);
      if (on && unlock()) play(1.5);
      else pause();
      refreshMaster();
    },
    async useFile(file) {
      if (file.size > MAX_FILE) throw new Error('这首歌太大了，换一首 30 MB 以内的');
      const c = ensure();
      if (!c) throw new Error('这个浏览器不支持播放音乐');
      try {
        buffer = await c.decodeAudioData(await file.arrayBuffer());
      } catch {
        throw new Error('这首歌读不出来，换一个 mp3 或 m4a 试试');
      }
      name = file.name.replace(/\.[^.]+$/, '') || '自己的歌';
      if (playing) {
        startSource();
        fadeTo(musicLevel(), 1.2);
      }
    },
    useDefault() {
      buffer = null;
      name = DEFAULT_NAME;
      if (playing && ctx) {
        startSource();
        fadeTo(musicLevel(), 1.2);
      }
    },
    heartbeat(period, strength = 1) {
      if (!effectLive()) return;
      const t = ctx!.currentTime + 0.02;
      thump(t + period * 0.04, 0.55 * strength);
      thump(t + period * 0.26, 0.32 * strength);
    },
    boom() {
      if (!effectLive()) return;
      const c = ctx!, t = c.currentTime + 0.02;
      const len = Math.floor(c.sampleRate * 2.4), nb = c.createBuffer(1, len, c.sampleRate), d = nb.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.2);
      const n = c.createBufferSource(), lp = c.createBiquadFilter(), g = c.createGain();
      n.buffer = nb;
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(2600, t);
      lp.frequency.exponentialRampToValueAtTime(90, t + 1.8);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.9, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 2.3);
      n.connect(lp);
      lp.connect(g);
      g.connect(sfxBus);
      n.start(t);
      n.stop(t + 2.4);
      const o = c.createOscillator(), og = c.createGain();
      o.frequency.setValueAtTime(64, t);
      o.frequency.exponentialRampToValueAtTime(26, t + 1.4);
      og.gain.setValueAtTime(0.0001, t);
      og.gain.exponentialRampToValueAtTime(1, t + 0.02);
      og.gain.exponentialRampToValueAtTime(0.0001, t + 1.9);
      o.connect(og);
      og.connect(sfxBus);
      o.start(t);
      o.stop(t + 2);
      // 一串上行的亮音，像星星被点亮
      [84, 88, 91, 96].forEach((m, k) => voice('sine', hz(m), t + 0.5 + k * 0.16, 0.005, 0.04, 0.05, 1.6, [sfxBus, reverb], 0, (k - 1.5) * 0.3));
    },
    chime() {
      if (!effectLive()) return;
      const t = ctx!.currentTime + 0.02;
      [84, 88, 91, 96].forEach((m, k) => voice('sine', hz(m), t + k * 0.16, 0.005, 0.04, 0.05, 1.6, [sfxBus, reverb], 0, (k - 1.5) * 0.3));
    },
    roamTick() {
      if (!effectLive()) return;
      // 一两个高音的短泛音，约 0.3 秒；峰值比 chime 的 0.04 轻很多，到站时轻轻点一下
      const t = ctx!.currentTime + 0.02;
      voice('sine', hz(96), t, 0.004, 0.014, 0.02, 0.28, [sfxBus, reverb]);
      voice('sine', hz(103), t + 0.03, 0.004, 0.005, 0.01, 0.2, [reverb]);
    },
    charge(seconds) {
      if (!effectLive()) return () => undefined;
      // 背景音乐先让一让
      const release = hold('photo');
      const c = ctx!, t = c.currentTime, o = c.createOscillator(), g = c.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(38, t);
      o.frequency.exponentialRampToValueAtTime(96, t + seconds);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.5, t + seconds);
      o.connect(g);
      g.connect(sfxBus);
      o.start(t);
      let stopped = false;
      return () => {
        if (stopped) return;
        stopped = true;
        const n = c.currentTime;
        g.gain.cancelScheduledValues(n);
        g.gain.setValueAtTime(g.gain.value, n);
        g.gain.exponentialRampToValueAtTime(0.0001, n + 0.25);
        o.stop(n + 0.3);
        release();
      };
    },
    shatter() {
      if (!effectLive()) return;
      const c = ctx!, t = c.currentTime + 0.01;
      // 一小段高通的噪声是碎的那一下，接一串很高、衰减很快的亮音是冰晶
      const len = Math.floor(c.sampleRate * 0.5), nb = c.createBuffer(1, len, c.sampleRate), d = nb.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
      const n = c.createBufferSource(), hp = c.createBiquadFilter(), g = c.createGain();
      n.buffer = nb;
      hp.type = 'highpass';
      hp.frequency.value = 2400;
      g.gain.value = 0.35;
      n.connect(hp);
      hp.connect(g);
      g.connect(sfxBus);
      n.start(t);
      for (let k = 0; k < 14; k++) voice('sine', 1800 + Math.random() * 3600, t + k * 0.03 + Math.random() * 0.02, 0.002, 0.05 * (1 - k / 16), 0.01, 0.4 + Math.random() * 0.6, [sfxBus, reverb], 0, (Math.random() - 0.5) * 1.4);
    },
    hush(on) {
      hushed = on;
      refreshMaster();
    },
    hold,
  };
}
